import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { ApiError } from "@/lib/api";
import {
  getTelegramStatus,
  initTelegramBinding,
  type TelegramInitResponse,
} from "@/lib/notifications-api";

/**
 * apps/web :: components :: TelegramBinding
 *
 * Modal-style panel rendered by NotificationPreferences when the user
 * flips the Telegram toggle ON. Drives the chat-id binding flow:
 *
 *   1. User clicks "Start binding" → POST /api/notifications/telegram/init
 *      returns a pairing code + `https://t.me/<bot>?start=<code>` deep link.
 *   2. We show the deep link as an "Open Telegram" button + render the URL
 *      beneath it for copy-paste (important for desktop users who don't
 *      have Telegram Desktop installed — they paste into their phone).
 *   3. We poll GET /status?code=<code> every 2s. On `verified: true` we
 *      call `onVerified()` which flips the parent toggle on + closes the
 *      modal. On timeout (5m) we show "Timed out" with a retry.
 *   4. "Cancel" stops polling and unconditionally closes.
 *
 * The component is deliberately dumb about the parent's preferences state —
 * all it does is drive the binding side-channel and call `onVerified()`
 * when the server says so. The parent owns flipping `telegramEnabled` + a
 * subsequent `PUT /preferences`.
 */

export interface TelegramBindingProps {
  /** Called after the backend reports `verified: true`. Closes the modal. */
  onVerified: () => void;
  /** Called if the user hits Cancel or Close. Stops polling; does NOT flip the toggle. */
  onCancel: () => void;
}

type BindingPhase =
  | { kind: "idle" }
  | { kind: "starting" }
  | { kind: "awaiting"; response: TelegramInitResponse }
  | { kind: "verified" }
  | { kind: "timed_out" }
  | { kind: "error"; message: string };

/** Poll cadence + total budget. Matches the 10-min server-side TTL envelope. */
const POLL_INTERVAL_MS = 2_000;
const POLL_BUDGET_MS = 5 * 60 * 1_000;

export default function TelegramBinding({
  onVerified,
  onCancel,
}: TelegramBindingProps) {
  const { t } = useTranslation();
  const [phase, setPhase] = useState<BindingPhase>({ kind: "idle" });
  // Keep a handle to the pending timers so Cancel + phase transitions can
  // clear them without racing.
  const timersRef = useRef<Array<ReturnType<typeof setTimeout>>>([]);

  const clearTimers = useCallback(() => {
    timersRef.current.forEach((id) => clearTimeout(id));
    timersRef.current = [];
  }, []);

  useEffect(() => () => clearTimers(), [clearTimers]);

  const pollUntilVerified = useCallback(
    (code: string) => {
      const startedAt = Date.now();
      const tick = async () => {
        try {
          const status = await getTelegramStatus(code);
          if (status.verified) {
            setPhase({ kind: "verified" });
            // Tiny delay so users see the "Linked." flash before close.
            const t1 = setTimeout(() => onVerified(), 400);
            timersRef.current.push(t1);
            return;
          }
        } catch (err) {
          if (err instanceof ApiError && err.status === 404) {
            setPhase({ kind: "timed_out" });
            return;
          }
          // Transient error — keep polling; surface only on final timeout.
        }
        if (Date.now() - startedAt >= POLL_BUDGET_MS) {
          setPhase({ kind: "timed_out" });
          return;
        }
        const t2 = setTimeout(tick, POLL_INTERVAL_MS);
        timersRef.current.push(t2);
      };
      const t0 = setTimeout(tick, POLL_INTERVAL_MS);
      timersRef.current.push(t0);
    },
    [onVerified],
  );

  async function handleStart() {
    setPhase({ kind: "starting" });
    clearTimers();
    try {
      const response = await initTelegramBinding();
      setPhase({ kind: "awaiting", response });
      pollUntilVerified(response.code);
    } catch (err) {
      const message =
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : String(err);
      setPhase({ kind: "error", message });
    }
  }

  function handleCancel() {
    clearTimers();
    onCancel();
  }

  // --- render --------------------------------------------------------------

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="telegram-binding-title"
      data-testid="telegram-binding-modal"
      className="fixed inset-0 z-50 flex items-center justify-center bg-brand-primary/40 p-md"
    >
      <div className="w-full max-w-md rounded-md border border-brand-primary/10 bg-white p-md shadow-lg">
        <h2
          id="telegram-binding-title"
          className="text-base font-semibold text-brand-primary"
        >
          {t("notifications.telegram.modalTitle")}
        </h2>
        <p className="mt-xs text-sm text-neutral-700">
          {t("notifications.telegram.intro")}
        </p>

        {phase.kind === "idle" && (
          <div className="mt-md flex flex-wrap gap-sm">
            <button
              type="button"
              onClick={() => void handleStart()}
              data-testid="telegram-start-binding"
              className="inline-flex items-center justify-center rounded-md bg-brand-primary px-md py-sm text-sm font-semibold text-white hover:bg-brand-primary/90"
            >
              {t("notifications.telegram.startBinding")}
            </button>
            <button
              type="button"
              onClick={handleCancel}
              data-testid="telegram-cancel"
              className="inline-flex items-center justify-center rounded-md border border-brand-primary/20 bg-white px-md py-sm text-sm font-medium text-brand-primary"
            >
              {t("notifications.telegram.cancel")}
            </button>
          </div>
        )}

        {phase.kind === "starting" && (
          <p
            role="status"
            className="mt-md text-sm text-neutral-700"
            data-testid="telegram-starting"
          >
            {t("notifications.telegram.waiting")}
          </p>
        )}

        {phase.kind === "awaiting" && (
          <div className="mt-md space-y-sm" data-testid="telegram-awaiting">
            <a
              href={phase.response.deepLink}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="telegram-open-link"
              className="inline-flex items-center justify-center rounded-md bg-brand-primary px-md py-sm text-sm font-semibold text-white hover:bg-brand-primary/90"
            >
              {t("notifications.telegram.openTelegram")}
            </a>
            <p className="text-xs text-neutral-600">
              {t("notifications.telegram.copyHint")}
            </p>
            <code
              data-testid="telegram-deep-link"
              className="block w-full break-all rounded-md bg-neutral-100 p-sm text-xs text-neutral-800"
            >
              {phase.response.deepLink}
            </code>
            <p
              role="status"
              aria-live="polite"
              className="text-sm text-neutral-700"
              data-testid="telegram-waiting"
            >
              {t("notifications.telegram.waiting")}
            </p>
            <button
              type="button"
              onClick={handleCancel}
              data-testid="telegram-cancel"
              className="inline-flex items-center justify-center rounded-md border border-brand-primary/20 bg-white px-md py-sm text-sm font-medium text-brand-primary"
            >
              {t("notifications.telegram.cancel")}
            </button>
          </div>
        )}

        {phase.kind === "verified" && (
          <p
            role="status"
            className="mt-md rounded-md border border-success/40 bg-success/5 p-md text-sm font-medium text-success"
            data-testid="telegram-verified"
          >
            {t("notifications.telegram.success")}
          </p>
        )}

        {phase.kind === "timed_out" && (
          <div className="mt-md space-y-sm" data-testid="telegram-timed-out">
            <p role="alert" className="text-sm font-medium text-danger">
              {t("notifications.telegram.timedOut")}
            </p>
            <div className="flex gap-sm">
              <button
                type="button"
                onClick={() => void handleStart()}
                data-testid="telegram-retry"
                className="inline-flex items-center justify-center rounded-md bg-brand-primary px-md py-sm text-sm font-semibold text-white hover:bg-brand-primary/90"
              >
                {t("notifications.telegram.startBinding")}
              </button>
              <button
                type="button"
                onClick={handleCancel}
                data-testid="telegram-close"
                className="inline-flex items-center justify-center rounded-md border border-brand-primary/20 bg-white px-md py-sm text-sm font-medium text-brand-primary"
              >
                {t("notifications.telegram.close")}
              </button>
            </div>
          </div>
        )}

        {phase.kind === "error" && (
          <div className="mt-md space-y-sm" data-testid="telegram-error">
            <p role="alert" className="text-sm font-medium text-danger">
              {t("notifications.telegram.error", { message: phase.message })}
            </p>
            <button
              type="button"
              onClick={handleCancel}
              data-testid="telegram-close"
              className="inline-flex items-center justify-center rounded-md border border-brand-primary/20 bg-white px-md py-sm text-sm font-medium text-brand-primary"
            >
              {t("notifications.telegram.close")}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
